<script lang="ts" setup>
import styled from 'vue-styled-components';
import { i18n } from '@/i18n';
import dayjs from 'dayjs';
import { computed, ComputedRef, onMounted, reactive, ref, watch } from 'vue';

// interface
interface Props {
  organizationId: string;
  month: string | number;
  year: string | number;
  activeClass: string;
  prefixClass: string;
  activeDates?: Array<any>;
  processMonthTitle?: (monthTitle: any, year: string | number) => string;
  lang?: string;
}

interface IMonthCalendar {
  showDays: any[];
  isMouseDown: boolean;
}

type ShowDays = {
  value: string | number;
  active: boolean;
  isOtherMonth: boolean;
  className: any;
};

type ProcessMonthTitleType = (monthTitle: any, year: string | number) => string;

// props
const props = withDefaults(defineProps<Props>(), {
  activeDates: () => [],
  processMonthTitle: (monthTitle: any) => String(monthTitle),
  lang: 'ja',
});

// emits
const emits = defineEmits<{
  (e: 'submit'): void;
  (
    e: 'toggleDate',
    dayObj: {
      year: string | number;
      month: string | number;
      date: any;
      selected: boolean;
      className: string;
      organizationId: string;
    }
  ): void;
  (
    e: 'monthClickEvent',
    monthYearInfo: {
      monthTitle: ComputedRef<any>;
      month: string | number;
      year: string | number;
    }
  ): void;
}>();

//変数
const state: IMonthCalendar = reactive<IMonthCalendar>({
  showDays: [],
  isMouseDown: false,
}) as IMonthCalendar;

//computed
const weekTitleFontSizeAdjustLang = computed(() => {
  const fontSizeMapping = {
    ja: '16px',
    tw: '16px',
    en: '14px',
    pt: '14px',
    de: '14px',
    es: '14px',
    pl: '12px',
    ru: '14px',
  };
  return fontSizeMapping[props.lang];
});
const monthTitle = computed(() => {
  const monthMapping = {
    ja: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'],
    tw: ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月'],
    en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    pt: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
    de: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
    es: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
    pl: [
      'Styczeń',
      'Luty',
      'Marzec',
      'Kwiecień',
      'Maj',
      'Czerwiec',
      'Lipiec',
      'Sierpień',
      'Wrzesień',
      'Październik',
      'Listopad',
      'Grudzień',
    ],
    ru: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
  };
  return monthMapping[props.lang][(Number(props.month) as number) - 1];
});
const displayMonthTitle = computed(() => {
  return props.processMonthTitle(monthTitle.value, props.year);
});

// watch
watch(
  () => props.year,
  () => {
    // yearRef.value = props.year;
    initCalendar();
  }
);

watch(
  () => props.activeDates,
  () => {
    // activeDatesRef.value = props.activeDates;
    initCalendar();
  }
);

// イベント
onMounted(() => {
  initCalendar();
});

//関数
const initCalendar = () => {
  if (!props.year || !props.month) return [];
  const activeMonth = dayjs()
    .set('date', 1)
    .set('year', Number(props.year) as number)
    .set('month', (Number(props.month) as number) - 1);
  let firstDay: number = activeMonth.startOf('month').day() - 1;
  if (firstDay < 0) firstDay += 7;
  const lastDate: number = activeMonth.endOf('month').date();
  const weekRow: number = firstDay >= 5 ? 6 : 5;
  const WEEK = 7;
  let day = 0;
  const fullCol = Array.from(Array(weekRow * WEEK).keys()).map((i) => {
    const value: string | number = firstDay <= i ? (day++ % lastDate) + 1 : '';
    return {
      value,
      active: false,
      isOtherMonth: firstDay > i || day > lastDate,
    };
  });
  state.showDays = fullCol;

  // 把 toggleDate 的內容合併在 initCalendar 裡。
  props.activeDates.forEach((date) => {
    let oDate;

    if (typeof date === 'string') {
      oDate = {
        date: date,
        className: props.activeClass,
      };
    } else if (typeof date === 'object') {
      oDate = date;
    }

    const dayjsObj: dayjs.Dayjs = dayjs(oDate.date);
    if (dayjsObj.year() !== props.year) return;
    const activeDate: number = dayjsObj.date();
    const row: number = Math.floor(activeDate / 7);
    const activeArrayKey: number = (activeDate % 7) - 1 + firstDay + 7 * row;
    state.showDays[activeArrayKey].active = true; // to array index
    state.showDays[activeArrayKey].className = oDate.className;
  });
};
const showDayTitle = (day: number): string => {
  const dayMapping: { [key: string]: string[] } = {
    ja: ['月', '火', '水', '木', '金', '土', '日'],
    tw: ['一', '二', '三', '四', '五', '六', '日'],
    en: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
    pt: ['2ª', '3ª', '4ª', '5ª', '6ª', 'Sa', 'Do'],
    de: ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'],
    es: ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'],
    pl: ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Nie'],
    ru: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
  };
  return dayMapping[props.lang][day];
};

const toggleDay = (dayObj: any) => {
  // if (dayObj.isOtherMonth) return
  emits('toggleDate', {
    year: props.year,
    month: props.month,
    date: dayObj.value,
    selected: !dayObj.active,
    className: props.activeClass,
    organizationId: props.organizationId,
  });
};

const dragDay = (dayObj: any): void => {
  if (state.isMouseDown) toggleDay(dayObj);
};

const mouseDown = (dayObj: any): void => {
  toggleDay(dayObj);
  state.isMouseDown = true;
};

const mouseUp = () => {
  state.isMouseDown = false;
};

const classList = (dayObj: any) => {
  const oClassList = {
    'calendar__day--otherMonth': dayObj.isOtherMonth,
    [props.prefixClass]: dayObj.active,
  };
  // console.log('classList', dayObj, this.prefixClass)
  if (dayObj.active) oClassList[dayObj.className] = true;
  else oClassList[dayObj.className] = false;
  return oClassList;
};

const monthClickEvent = (year: string | number): void => {
  const monthYearInfo = {
    monthTitle: monthTitle,
    month: props.month,
    year: year,
  };
  emits('monthClickEvent', monthYearInfo);
};
</script>

<template>
  <div class="c-wrapper">
    <div class="calendar" @mouseup="mouseUp" @mouseleave.stop="mouseUp">
      <div class="calendar__title" @click="monthClickEvent(year)">{{ displayMonthTitle }}</div>
      <div class="calendar__body">
        <div
          v-for="(day, key) in 7"
          :key="`title${day}`"
          class="calendar__day day__weektitle"
          :style="{ fontSize: weekTitleFontSizeAdjustLang }"
        >
          {{ showDayTitle(key) }}
        </div>
        <div v-for="(dayObj, key) in state.showDays" :key="`day${key}`" class="calendar__day">
          <div class="day" :class="classList(dayObj)" @mouseover="dragDay(dayObj)" @mousedown="mouseDown(dayObj)">
            {{ dayObj.value }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="css" scoped>
.c-wrapper {
  padding: 10px;
}
.calendar {
  background-color: #fff;
  min-height: 295px;
  text-align: center;
  color: rgba(53, 60, 70, 0.8);
  border-radius: 2px;
  min-width: 0;
  position: relative;
  text-decoration: none;
  box-shadow: 0 2px 1px -1px rgba(0, 0, 0, 0.2), 0 1px 1px 0 rgba(0, 0, 0, 0.14), 0 1px 3px 0 rgba(0, 0, 0, 0.12);
  transition: transform 0.3s ease;
}
.calendar:hover {
  z-index: 2;
}
@media (min-width: 1024px) {
  .calendar:hover {
    transform: scale(1.15);
    box-shadow: 0 7px 21px 0 rgba(0, 0, 0, 0.1);
  }
}
.calendar .calendar__title {
  font-weight: bold;
  flex: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid rgba(196, 196, 196, 0.3);
  font-size: 18px;
  height: 50px;
  margin-bottom: 12px;
  cursor: pointer;
}
.calendar .calendar__body {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-content: flex-start;
  padding: 0px 20px;
  min-width: 194px;
}
.calendar .calendar__day {
  flex: 14.28%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  height: 31px;
  color: #5db3d4;
}
.calendar .day__weektitle {
  color: rgba(53, 60, 70, 0.8);
}
.calendar .day {
  font-size: 14px;
  cursor: pointer;
  user-select: none;
  width: 22px;
  height: 22px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  border-radius: 5px;
}
.calendar .day:after {
  content: '';
  display: block;
  height: 10px;
  width: 10px;
  position: absolute;
  top: -5px;
  right: -5px;
  border-radius: 50%;
  z-index: 1;
  background-color: transparent;
}
.calendar .day.calendar--active:after {
  background-image: url('../assets/baseline-remove_circle-24px.svg');
  background-size: 100% 100%;
}
.calendar .day:not(.calendar__day--otherMonth):hover {
  background-color: rgba(102, 102, 102, 0.1);
  border-radius: 5px;
}
.calendar .day.calendar--active {
  /* background-color: rgba(255,186,186,0.5); */
  color: #bcbcbc;
}
.calendar .day.calendar--active.info {
  background-color: rgba(23, 162, 184, 0.8);
  color: #fff;
}
.calendar .day.calendar--active.info:after {
  background-image: url('../assets/RecordIt.svg');
  background-size: 100% 100%;
}
.calendar .day.calendar--active.warning {
  background-color: rgba(255, 193, 7, 0.7);
  color: #fff;
}
.calendar .day.calendar--active.warning:after {
  background-image: url('../assets/round-warning-24px.svg');
  background-color: rgba(234, 234, 234, 0.3);
  background-size: 100% 100%;
}
.calendar .calendar__day--otherMonth {
  color: #eaeaea;
  cursor: auto;
}

.yellow {
  background-color: rgba(255, 193, 7, 0.7);
}
.red {
  background-color: rgba(255, 186, 186, 0.5);
}
</style>
